import { textFragment } from '../../graphql/fragments/textFragment';
import { weblinkFragment } from '../../graphql/fragments/weblinkFragment';
import { graphql } from '../../graphql/graphql';

const helmetFragment = graphql(`
  fragment Helmet on SuburbPage_Page {
    helmet {
      canonical
      title
      description
      siteName
    }
  }
`);

const breadCrumbsFragment = graphql(`
  fragment BreadCrumbs on SuburbPage_Page {
    breadCrumbs {
      title
      url
    }
  }
`);

const titleFragment = graphql(`
  fragment Title on SuburbPage_Page {
    title {
      parts
    }
  }
`);

const marketInsightsFragment = graphql(`
  fragment MarketInsights on SuburbPage_Page {
    marketInsights {
      title
      subtitle
      propertyTypes
      defaultPropertyType
      priceTrends {
        propertyType
        medianPriceSubBrick {
          title
          subtitle
          disclaimer
          cost {
            title
            suffix
          }
          costChange {
            title
            value
            iconNameV2
            variant
          }
          additionalDetails {
            title
            displayValue
          }
          graph {
            points {
              date
              value
              hover {
                label
                title
                subtitle
              }
            }
            variant
          }
        }
        medianRentSubBrick {
          title
          subtitle
          disclaimer
          cost {
            title
            suffix
          }
          costChange {
            title
            value
            iconNameV2
            variant
          }
          additionalDetails {
            title
            displayValue
          }
          graph {
            points {
              date
              value
              hover {
                label
                title
                subtitle
              }
            }
            variant
          }
        }
      }
      description {
        paragraphs
        expansionToggle {
          visible
          text
          buttonVariant
        }
      }
    }
  }
`);

const demographicsFragment = graphql(
  `
    fragment Demographics on SuburbPage_Page {
      demographics {
        title
        subtitle {
          ...Text
        }
        disclaimer
        populationTile {
          title
          valueWithUnit {
            value
            unit
          }
          additionalInfo {
            value
            suffix
            variant
            lineVariant
          }
        }
        incomeTile {
          title
          valueWithUnit {
            value
            unit
          }
          additionalInfo {
            value
            suffix
            variant
            lineVariant
          }
        }
        ageGroupsSubBrick {
          title
          subtitle
          legendEntries {
            label {
              ...Text
            }
            colorVariant
          }
          barGraphBars {
            label
            value
            colorVariant
          }
        }
        householdsSubBrick {
          title
          subtitle
          legendEntries {
            label {
              ...Text
            }
            colorVariant
          }
          pieGraphSlices {
            value
            colorVariant
          }
        }
        propertyOwnershipSubBrick {
          title
          subtitle
          barGraphBars {
            colorVariant
            barLabel
            categoryLabel
            value
          }
        }
      }
    }
  `,
  [textFragment],
);

const propertyCollectionsFragment = graphql(`
  fragment PropertyCollections on SuburbPage_Page {
    propertyCollections {
      subtitle
      title
      collectionType
      properties {
        address
        path
        image {
          alt
          jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
          webpUri: uri(width: 224, height: 126, format: WEBP, imageMode: Crop, progressive: true)
        }
        badge {
          text
          variant
        }
        priceV2 {
          display
          sourceLevel
        }
        features {
          iconNameV2
          title
          hoverTitle
          valueWithUnit
        }
        soldDate
        clickThroughTracking {
          pathname
          page
          element
          sourceType
        }
      }
      tracking {
        showMoreProperties {
          schema
          data {
            event_name
            in_page_source
            user_action
          }
        }
      }
      searchPageWebLink {
        title
        url
        isExternal
        variant
        tracking {
          clickThroughTracking {
            pathname
            page
            element
            sourceType
          }
        }
        key
      }
    }
  }
`);

const nearbyChildcareCentresFragment = graphql(`
  fragment NearbyChildcareCentres on SuburbPage_Page {
    nearbyChildcareCentres {
      title
      subtitle
      centreFactHeader
      centres {
        isDefaultOpen
        tracking {
          expand {
            schema
            data {
              event_name
              in_page_source
              user_action
              accordion_copy
            }
          }
          collapse {
            schema
            data {
              event_name
              in_page_source
              user_action
              accordion_copy
            }
          }
        }
        centre {
          name
          vacancyBadge {
            text
            variant
          }
          openingHours
          address
          webLinks {
            title
            url
            isExternal
            variant
            key
            tracking {
              outboundTracking {
                schema
                data {
                  event_name
                  type
                  click_url
                }
              }
              clickThroughTracking {
                pathname
                page
                element
                sourceType
              }
            }
          }
          childcareFacts {
            ... on Fact {
              title
              subtitle
            }
            ... on StarRatingFact {
              displayStarRatingPercentage
              accessibilityTitle
              title
              subtitle
            }
          }
        }
      }
    }
  }
`);

const nearbySchoolsFragment = graphql(`
  fragment NearbySchools on SuburbPage_Page {
    nearbySchools {
      title
      schoolFactHeader
      schools {
        visible {
          ...School
        }
        hidden {
          ...School
        }
      }
      showAllButton {
        text
        buttonVariant
        ariaLabel
        iconName
        iconPlacement
        sizeVariant
        buttonType
        trackingEvent
      }
    }
  }

  fragment School on SchoolCollection_School {
    schoolName
    isDefaultOpen
    isZoned
    schoolFacts {
      title
      subtitle
    }
    webLinks {
      title
      url
      isExternal
      variant
      key
      iconInfo {
        name
        placement
      }
      tracking {
        outboundTracking {
          schema
          data {
            event_name
            type
            click_url
          }
        }
        clickThroughTracking {
          pathname
          page
          element
          sourceType
        }
      }
    }
    tracking {
      expand {
        schema
        data {
          event_name
          in_page_source
          user_action
          accordion_copy
        }
      }
      collapse {
        schema
        data {
          event_name
          in_page_source
          user_action
          accordion_copy
        }
      }
    }
  }
`);

const streetsFragment = graphql(`
  fragment Streets on SuburbPage_Page {
    streets {
      title
      subtitle
      items {
        visible {
          link {
            url
            title
          }
        }
        hidden {
          link {
            url
            title
          }
        }
      }
      showMoreButton {
        title
      }
    }
  }
`);

const surroundingSuburbsFragment = graphql(`
  fragment SurroundingSuburbs on SuburbPage_Page {
    surroundingSuburbs {
      title
      subtitle
      items {
        visible {
          link {
            url
            title
            clickThroughTracking {
              pathname
              page
              element
              sourceType
            }
          }
        }
        hidden {
          link {
            url
            title
            clickThroughTracking {
              pathname
              page
              element
              sourceType
            }
          }
        }
      }
      showMoreButton {
        title
      }
    }
  }
`);

const seoFooterFragment = graphql(
  `
    fragment SeoFooter on SuburbPage_Page {
      seoFooter {
        footerTabsV2 {
          title
          tabs {
            name
            tabContent {
              title
              links {
                ...WebLink
              }
            }
          }
        }
      }
    }
  `,
  [weblinkFragment],
);

export const suburbPageQuery = graphql(
  `
    query SuburbPageBySlug($slug: SuburbPageSlug!) {
      suburbPageBySlug(slug: $slug) {
        id
        slug
        ...Helmet
        ...BreadCrumbs
        ...Title
        ...Demographics
        ...MarketInsights
        ...PropertyCollections
        ...NearbyChildcareCentres
        ...NearbySchools
        ...Streets
        ...SurroundingSuburbs
        ...SeoFooter
        vgDisclaimerHtml
      }
    }
  `,
  [
    helmetFragment,
    breadCrumbsFragment,
    titleFragment,
    demographicsFragment,
    marketInsightsFragment,
    propertyCollectionsFragment,
    nearbyChildcareCentresFragment,
    nearbySchoolsFragment,
    streetsFragment,
    surroundingSuburbsFragment,
    seoFooterFragment,
  ],
);
